import React, { useEffect, useState } from 'react';
import DateField from '../../components/DateField';
import {
  listBillingAccounts, setBillingExpiration, listBillingPayments, recordBillingPayment,
  recordManualBillingCredit, listCompanyBillingCredits,
} from '../../api/billing';
import { todayISO } from '../../utils/date';

const money = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
};

const apiDetail = (err, fallback) => {
  try {
    return JSON.parse(err.message).detail || fallback;
  } catch (parseErr) {
    return fallback;
  }
};

/**
 * Encaissements par company (demande 2026-10-18) : le systeme donne le montant
 * (banks actives x tarif + balance reportee du dernier encaissement), le
 * superadmin saisit le montant RECU et la NOUVELLE date d'expiration (saisie
 * 100% manuelle). Paiement partiel accepte : la date est prolongee, la balance
 * passe sur le cycle suivant. Chaque company a sa propre date — une fois
 * posee, c'est elle qui pilote le blocage automatique de cette company (plus
 * les factures par periode du rapport plus bas).
 */
const BillingAccountsCard = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [receivedDrafts, setReceivedDrafts] = useState({});
  const [expiryDrafts, setExpiryDrafts] = useState({});
  const [historyFor, setHistoryFor] = useState(null);
  const [history, setHistory] = useState([]);
  const [advanceDrafts, setAdvanceDrafts] = useState({});
  const [advanceNotes, setAdvanceNotes] = useState({});
  const [creditHistoryFor, setCreditHistoryFor] = useState(null);
  const [creditHistory, setCreditHistory] = useState([]);

  const resetDrafts = (rows) => {
    const expiry = {};
    rows.forEach((a) => { expiry[a.company_id] = a.expires_at || ''; });
    setExpiryDrafts(expiry);
    setReceivedDrafts({});
  };

  useEffect(() => {
    listBillingAccounts()
      .then((rows) => { setAccounts(rows); resetDrafts(rows); })
      .catch(() => setError('Impossible de charger les encaissements.'))
      .finally(() => setLoading(false));
  }, []);

  const replaceRow = (row) => {
    setAccounts((prev) => prev.map((a) => (a.company_id === row.company_id ? row : a)));
    setExpiryDrafts((prev) => ({ ...prev, [row.company_id]: row.expires_at || '' }));
    setReceivedDrafts((prev) => ({ ...prev, [row.company_id]: '' }));
  };

  const loadHistory = async (companyId) => {
    const rows = await listBillingPayments(companyId);
    setHistory(rows);
    setHistoryFor(companyId);
  };

  const toggleHistory = async (companyId) => {
    if (historyFor === companyId) {
      setHistoryFor(null);
      return;
    }
    setError('');
    try {
      await loadHistory(companyId);
    } catch (err) {
      setError('Historique indisponible.');
    }
  };

  const savePayment = async (account) => {
    const received = receivedDrafts[account.company_id];
    const expiresAt = expiryDrafts[account.company_id];
    if (received === undefined || received === '' || Number.isNaN(Number(received)) || Number(received) < 0) {
      setError(`${account.company_name} : montant reçu invalide.`);
      return;
    }
    if (!expiresAt) {
      setError(`${account.company_name} : entre la nouvelle date d'expiration.`);
      return;
    }
    setBusyId(account.company_id);
    setError('');
    try {
      const row = await recordBillingPayment(account.company_id, { amountReceived: received, expiresAt });
      replaceRow(row);
      if (historyFor === account.company_id) await loadHistory(account.company_id);
    } catch (err) {
      setError(apiDetail(err, 'Enregistrement impossible.'));
    } finally {
      setBusyId(null);
    }
  };

  // Changer seulement la date (mise en place initiale, prolongation
  // exceptionnelle) SANS encaissement — ne cree aucune ligne d'historique et
  // ne facture pas de cycle.
  const saveExpiryOnly = async (account) => {
    setBusyId(account.company_id);
    setError('');
    try {
      const row = await setBillingExpiration(account.company_id, expiryDrafts[account.company_id]);
      replaceRow(row);
    } catch (err) {
      setError(apiDetail(err, 'Changement de date impossible.'));
    } finally {
      setBusyId(null);
    }
  };

  const saveManualCredit = async (account) => {
    const companyId = account.company_id;
    const amount = advanceDrafts[companyId];
    const note = advanceNotes[companyId] || '';

    if (amount === undefined || amount === '' ||
        !Number.isFinite(Number(amount)) || Number(amount) <= 0) {
      setError(`${account.company_name} : montant d'avance invalide.`);
      return;
    }

    setBusyId(companyId);
    setError('');
    try {
      await recordManualBillingCredit(companyId, amount, note);
      setAdvanceDrafts((prev) => ({ ...prev, [companyId]: '' }));
      setAdvanceNotes((prev) => ({ ...prev, [companyId]: '' }));
      if (creditHistoryFor === companyId) {
        const result = await listCompanyBillingCredits(companyId);
        setCreditHistory(result.credits || []);
      }
    } catch (err) {
      setError(apiDetail(err, 'Enregistrement de l’avance impossible.'));
    } finally {
      setBusyId(null);
    }
  };

  const toggleCreditHistory = async (companyId) => {
    if (creditHistoryFor === companyId) {
      setCreditHistoryFor(null);
      return;
    }
    setError('');
    try {
      const result = await listCompanyBillingCredits(companyId);
      setCreditHistory(result.credits || []);
      setCreditHistoryFor(companyId);
    } catch (err) {
      setError(apiDetail(err, 'Historique des avances indisponible.'));
    }
  };

  const today = todayISO();

  return (
    <div className="sb-card">
      <div className="sb-card-header">
        <span className="sb-card-title">Encaissements</span>
      </div>
      <div className="sb-card-body">
        <p className="sb-hint">
          Montant = banks actives × tarif + balance reportée. Entre le montant reçu et la nouvelle date
          d'expiration, puis « Encaisser ». S'il reste une balance, elle s'ajoute au prochain encaissement.
          Chaque company a sa propre date : elle est bloquée à partir du lendemain de sa date d'expiration.
          « Date seule » change la date sans encaissement (mise en place, prolongation exceptionnelle).
        </p>
        {error && <p className="sb-error">{error}</p>}
        {loading ? (
          <p>Chargement...</p>
        ) : (
          <div className="sb-table-wrap">
            <table className="sb-table">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Banks actives</th>
                  <th>Tarif</th>
                  <th>Montant du cycle</th>
                  <th>Balance reportée</th>
                  <th>Total à payer</th>
                  <th>Expire le</th>
                  <th>Montant reçu</th>
                  <th>Nouvelle expiration</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {accounts.length === 0 && (
                  <tr><td colSpan={10} className="sb-empty">Aucune company avec un tarif configuré.</td></tr>
                )}
                {accounts.map((a) => {
                  const expired = a.expires_at && a.expires_at < today;
                  const busy = busyId === a.company_id;
                  return (
                    <React.Fragment key={a.company_id}>
                      <tr>
                        <td>
                          {a.company_name}
                          {a.billing_blocked && <div className="sb-balance-due">Bloquée</div>}
                        </td>
                        <td className="sb-center">{a.bank_count}</td>
                        <td className="sb-money">{money(a.rate_per_bank)}</td>
                        <td className="sb-money">{money(a.cycle_amount)}</td>
                        <td className={`sb-money ${Number(a.previous_balance) > 0 ? 'sb-balance-due' : ''}`}>
                          {money(a.previous_balance)}
                        </td>
                        <td className="sb-money"><strong>{money(a.total_due)}</strong></td>
                        <td className={`sb-center ${expired ? 'sb-balance-due' : ''}`}>
                          {a.expires_at ? fmtDate(a.expires_at) : 'Non définie'}
                        </td>
                        <td>
                          <input
                            className="sb-input sb-input-sm"
                            type="text"
                            inputMode="decimal"
                            placeholder={money(a.total_due)}
                            value={receivedDrafts[a.company_id] ?? ''}
                            onChange={(e) => setReceivedDrafts((prev) => ({ ...prev, [a.company_id]: e.target.value }))}
                            disabled={busy}
                          />
                        </td>
                        <td>
                          <DateField
                            className="sb-input sb-input-date"
                            value={expiryDrafts[a.company_id] || ''}
                            onChange={(v) => setExpiryDrafts((prev) => ({ ...prev, [a.company_id]: v }))}
                          />
                        </td>
                        <td>
                          <button type="button" className="sb-save-btn" disabled={busy} onClick={() => savePayment(a)}>
                            {busy ? '...' : 'Encaisser'}
                          </button>
                          {' '}
                          <button
                            type="button"
                            className="sb-unblock-btn"
                            disabled={busy || (expiryDrafts[a.company_id] || '') === (a.expires_at || '')}
                            onClick={() => saveExpiryOnly(a)}
                          >
                            Date seule
                          </button>
                          {' '}
                          <button type="button" className="sb-block-btn" onClick={() => toggleHistory(a.company_id)}>
                            {historyFor === a.company_id ? 'Masquer' : 'Historique'}
                          </button>
                        </td>
                      </tr>
                      {historyFor === a.company_id && (
                        <tr>
                          <td colSpan={10}>
                            {history.length === 0 ? (
                              <p className="sb-empty">Aucun encaissement enregistré.</p>
                            ) : (
                              <table className="sb-table">
                                <thead>
                                  <tr>
                                    <th>Date</th>
                                    <th>Banks</th>
                                    <th>Cycle</th>
                                    <th>Balance précédente</th>
                                    <th>Total dû</th>
                                    <th>Reçu</th>
                                    <th>Balance reportée</th>
                                    <th>Expiration</th>
                                    <th>Par</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {history.map((h) => (
                                    <tr key={h.id}>
                                      <td>{fmtDate(h.created_at)}</td>
                                      <td className="sb-center">{h.bank_count}</td>
                                      <td className="sb-money">{money(h.cycle_amount)}</td>
                                      <td className="sb-money">{money(h.previous_balance)}</td>
                                      <td className="sb-money">{money(h.total_due)}</td>
                                      <td className="sb-money">{money(h.amount_received)}</td>
                                      <td className={`sb-money ${Number(h.balance_after) > 0 ? 'sb-balance-due' : 'sb-balance-clear'}`}>
                                        {money(h.balance_after)}
                                      </td>
                                      <td className="sb-center">{fmtDate(h.expires_before)} → {fmtDate(h.expires_after)}</td>
                                      <td>{h.recorded_by}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </td>
                        </tr>
                      )}
                                        <tr>
                      <td colSpan={10}>
                        <div className="sb-credit-entry">
                          <strong>Avance manuelle (USD)</strong>
                          <input
                            className="sb-input sb-input-sm"
                            type="text"
                            inputMode="decimal"
                            placeholder="Montant de l'avance"
                            value={advanceDrafts[a.company_id] ?? ''}
                            onChange={(e) => setAdvanceDrafts((prev) => ({
                              ...prev, [a.company_id]: e.target.value,
                            }))}
                            disabled={busy}
                          />
                          <input
                            className="sb-input sb-input-sm"
                            type="text"
                            maxLength={255}
                            placeholder="Note (facultatif)"
                            value={advanceNotes[a.company_id] ?? ''}
                            onChange={(e) => setAdvanceNotes((prev) => ({
                              ...prev, [a.company_id]: e.target.value,
                            }))}
                            disabled={busy}
                          />
                          <button
                            type="button"
                            className="sb-save-btn"
                            disabled={busy}
                            onClick={() => saveManualCredit(a)}
                          >
                            {busy ? '...' : 'Ajouter avance'}
                          </button>
                          <button
                            type="button"
                            className="sb-block-btn"
                            disabled={busy}
                            onClick={() => toggleCreditHistory(a.company_id)}
                          >
                            {creditHistoryFor === a.company_id
                              ? 'Masquer crédits' : 'Historique crédits'}
                          </button>
                        </div>
                        {creditHistoryFor === a.company_id && (
                          creditHistory.length === 0 ? (
                            <p className="sb-empty">Aucune avance enregistrée.</p>
                          ) : (
                            <table className="sb-table">
                              <thead>
                                <tr>
                                  <th>Date</th>
                                  <th>Avance initiale</th>
                                  <th>Déjà utilisée</th>
                                  <th>Crédit restant</th>
                                  <th>Note</th>
                                  <th>Par</th>
                                </tr>
                              </thead>
                              <tbody>
                                {creditHistory.map((c) => (
                                  <tr key={c.id}>
                                    <td>{fmtDate(c.created_at)}</td>
                                    <td className="sb-money">{money(c.original_amount)}</td>
                                    <td className="sb-money">{money(c.used_amount)}</td>
                                    <td className="sb-money">{money(c.remaining_amount)}</td>
                                    <td>{c.note || '—'}</td>
                                    <td>{c.created_by || '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )
                        )}
                      </td>
                    </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default BillingAccountsCard;
